<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractGeneralInfo',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_6379803495938895_to = computed(() => ({
    name: 'app.company',
    params: {
      id: props.contract.company.id,
    },
  }));
  const b_8906220998092919_to = computed(() => ({
    name: 'app.company',
    params: {
      id: props.contract.counterparty.id,
    },
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_6379803495938895_to: b_6379803495938895_to.value,
    b_8906220998092919_to: b_8906220998092919_to.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_6379803495938895_to', nodeId: '6379803495938895' },
      { name: 'b_8906220998092919_to', nodeId: '8906220998092919' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_6379803495938895_to, b_8906220998092919_to },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_8pfz49vtx_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_f10a9s3j2_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_ww1vub18p_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'general',
        contract: _.cloneDeep(props.contract),
      },
    };
  };
  const act_7349226261686150_emit_b5wtks395_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5937240531526321_emit_ky0kxa4up_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_5937240531526321_close_q56q40oe1_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_5937240531526321_showModal_w69vq1n3a_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'company',
        contract: _.cloneDeep(props.contract),
      },
    };
  };
  const act_7527485200259320_emit_1aqgjbj5t_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_7527485200259320_close_vpgujoxk0_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_7527485200259320_showModal_yimv25fb4_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'counterparty',
        contract: _.cloneDeep(props.contract),
      },
    };
  };
  const act_7127218473441393_emit_o6l0lwysv_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_7127218473441393_close_k202d53ij_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_7127218473441393_showModal_eiue5lu08_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'rental',
        contract: props.contract.rental
          ? _.cloneDeep(props.contract)
          : _.extend(_.cloneDeep(props.contract), {
              rental: {
                id: null,
                name: null,
                number: null,
                description: null,
                house_number: null,
                inactive: false,
                location_code: null,
                lot_number: null,
              },
            }),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_d28dlsmha =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_3w18i92cn =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_d28dlsmha,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_3w18i92cn],
    event: 'reload',
  });
  const act_2095089031469157_emit_8pfz49vtx = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_8pfz49vtx_getActionArgs,
  });
  const inst_evh_2095089031469157_close_f10a9s3j2 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_8pfz49vtx,
      event: 'close',
      when: inst_evh_2095089031469157_close_f10a9s3j2_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_ww1vub18p =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_ww1vub18p_getActionArgs,
      events: [inst_evh_2095089031469157_close_f10a9s3j2],
    });
  const inst_evh_2095089031469157_clickPreventStop_t90ec1c62 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_ww1vub18p,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_t90ec1c62],
      event: 'click.prevent.stop',
    });
  const act_7349226261686150_emit_b5wtks395 = new actionTypes.EmitAction({
    actionArgs: act_7349226261686150_emit_b5wtks395_getActionArgs,
  });
  const inst_evh_7349226261686150_reload_12jk7b114 =
    new actionTypes.EventHandlerImpl({
      action: act_7349226261686150_emit_b5wtks395,
      event: 'reload',
      displayName: 'emit',
    });
  const inst_evh_7349226261686150_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7349226261686150_reload_12jk7b114],
    event: 'reload',
  });
  const act_5937240531526321_emit_ky0kxa4up = new actionTypes.EmitAction({
    actionArgs: act_5937240531526321_emit_ky0kxa4up_getActionArgs,
  });
  const inst_evh_5937240531526321_close_q56q40oe1 =
    new actionTypes.EventHandlerImpl({
      action: act_5937240531526321_emit_ky0kxa4up,
      event: 'close',
      when: inst_evh_5937240531526321_close_q56q40oe1_getWhen,
      displayName: 'emit',
    });
  const act_5937240531526321_showModal_w69vq1n3a =
    new actionTypes.ShowModalAction({
      actionArgs: act_5937240531526321_showModal_w69vq1n3a_getActionArgs,
      events: [inst_evh_5937240531526321_close_q56q40oe1],
    });
  const inst_evh_5937240531526321_clickPreventStop_mg3c6hqs6 =
    new actionTypes.EventHandlerImpl({
      action: act_5937240531526321_showModal_w69vq1n3a,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5937240531526321_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5937240531526321_clickPreventStop_mg3c6hqs6],
      event: 'click.prevent.stop',
    });
  const act_7527485200259320_emit_1aqgjbj5t = new actionTypes.EmitAction({
    actionArgs: act_7527485200259320_emit_1aqgjbj5t_getActionArgs,
  });
  const inst_evh_7527485200259320_close_vpgujoxk0 =
    new actionTypes.EventHandlerImpl({
      action: act_7527485200259320_emit_1aqgjbj5t,
      event: 'close',
      when: inst_evh_7527485200259320_close_vpgujoxk0_getWhen,
      displayName: 'emit',
    });
  const act_7527485200259320_showModal_yimv25fb4 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7527485200259320_showModal_yimv25fb4_getActionArgs,
      events: [inst_evh_7527485200259320_close_vpgujoxk0],
    });
  const inst_evh_7527485200259320_clickPreventStop_784k9ayms =
    new actionTypes.EventHandlerImpl({
      action: act_7527485200259320_showModal_yimv25fb4,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7527485200259320_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7527485200259320_clickPreventStop_784k9ayms],
      event: 'click.prevent.stop',
    });
  const act_7127218473441393_emit_o6l0lwysv = new actionTypes.EmitAction({
    actionArgs: act_7127218473441393_emit_o6l0lwysv_getActionArgs,
  });
  const inst_evh_7127218473441393_close_k202d53ij =
    new actionTypes.EventHandlerImpl({
      action: act_7127218473441393_emit_o6l0lwysv,
      event: 'close',
      when: inst_evh_7127218473441393_close_k202d53ij_getWhen,
      displayName: 'emit',
    });
  const act_7127218473441393_showModal_eiue5lu08 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7127218473441393_showModal_eiue5lu08_getActionArgs,
      events: [inst_evh_7127218473441393_close_k202d53ij],
    });
  const inst_evh_7127218473441393_clickPreventStop_2f3v4ec7o =
    new actionTypes.EventHandlerImpl({
      action: act_7127218473441393_showModal_eiue5lu08,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7127218473441393_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7127218473441393_clickPreventStop_2f3v4ec7o],
      event: 'click.prevent.stop',
    });
  const act_4198689235226718_reloadComponentData_38v57642f =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_4198689235226718_reload_dt64s7s60 =
    new actionTypes.EventHandlerImpl({
      action: act_4198689235226718_reloadComponentData_38v57642f,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_4198689235226718_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4198689235226718_reload_dt64s7s60],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_3w18i92cn,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_f10a9s3j2,
    inst_evh_2095089031469157_clickPreventStop_t90ec1c62,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_7349226261686150_reload_12jk7b114,
    inst_evh_7349226261686150_reload,
    inst_evh_5937240531526321_close_q56q40oe1,
    inst_evh_5937240531526321_clickPreventStop_mg3c6hqs6,
    inst_evh_5937240531526321_clickPreventStop,
    inst_evh_7527485200259320_close_vpgujoxk0,
    inst_evh_7527485200259320_clickPreventStop_784k9ayms,
    inst_evh_7527485200259320_clickPreventStop,
    inst_evh_7127218473441393_close_k202d53ij,
    inst_evh_7127218473441393_clickPreventStop_2f3v4ec7o,
    inst_evh_7127218473441393_clickPreventStop,
    inst_evh_4198689235226718_reload_dt64s7s60,
    inst_evh_4198689235226718_reload,
  };

  const evh_7315092382398562_reload_3w18i92cn = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_3w18i92cn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_f10a9s3j2 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_f10a9s3j2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_t90ec1c62 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_t90ec1c62.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7349226261686150_reload_12jk7b114 = async (event, scope) => {
    return await actions.inst_evh_7349226261686150_reload_12jk7b114.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7349226261686150_reload = async (event, scope) => {
    return await actions.inst_evh_7349226261686150_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5937240531526321_close_q56q40oe1 = async (event, scope) => {
    return await actions.inst_evh_5937240531526321_close_q56q40oe1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5937240531526321_clickPreventStop_mg3c6hqs6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5937240531526321_clickPreventStop_mg3c6hqs6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5937240531526321_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5937240531526321_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7527485200259320_close_vpgujoxk0 = async (event, scope) => {
    return await actions.inst_evh_7527485200259320_close_vpgujoxk0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7527485200259320_clickPreventStop_784k9ayms = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7527485200259320_clickPreventStop_784k9ayms.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7527485200259320_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7527485200259320_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7127218473441393_close_k202d53ij = async (event, scope) => {
    return await actions.inst_evh_7127218473441393_close_k202d53ij.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7127218473441393_clickPreventStop_2f3v4ec7o = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7127218473441393_clickPreventStop_2f3v4ec7o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7127218473441393_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7127218473441393_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4198689235226718_reload_dt64s7s60 = async (event, scope) => {
    return await actions.inst_evh_4198689235226718_reload_dt64s7s60.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4198689235226718_reload = async (event, scope) => {
    return await actions.inst_evh_4198689235226718_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Contract', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Contract number')"
          :content="contract.contract_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Contract', 'Contract type')">
          <UcContractTypeBadge :value="contract.contract_type">
          </UcContractTypeBadge>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Contract', 'Status')">
          <UcContractStatusCard
            :contract="contract"
            :reload="lastLoaded"
            @reload="evh_7349226261686150_reload($event, {})"
          >
          </UcContractStatusCard>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Description')"
          :content="contract.extras.description"
          v-if="contract.extras.description"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Folder number')"
          :content="contract.extras.folder_number"
          v-if="contract.extras.folder_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Reference number')"
          :content="contract.extras.reference_number"
          v-if="contract.extras.reference_number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Lessor')"
          expandable
        >
          <ItpBox>
            <ItpLink :text="contract.company.name" :to="b_6379803495938895_to">
            </ItpLink>
            <ItpButton
              :text="gettext('Edit...')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!readonly"
              @click.prevent.stop="
                evh_5937240531526321_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </ItpBox>
          <template #expander>
            <UcContractCompanyCard :item="contract.company">
            </UcContractCompanyCard>
          </template>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Tenant')"
          expandable
        >
          <ItpBox>
            <ItpLink
              :text="contract.counterparty.name"
              :to="b_8906220998092919_to"
            >
            </ItpLink>
            <ItpButton
              :text="gettext('Edit...')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!readonly"
              @click.prevent.stop="
                evh_7527485200259320_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </ItpBox>
          <template #expander>
            <UcContractClientCard :item="contract.counterparty">
            </UcContractClientCard>
          </template>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Rental')"
          expandable
          :expander-cols="11"
        >
          <ItpBox>
            <ItpLink
              :text="contract.rental.name"
              :to="{
                name: 'app.rental',
                params: {
                  id: contract.rental.id,
                },
              }"
              v-if="contract.rental"
            >
            </ItpLink>
            <ItpButton
              :text="gettext('Edit...')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!readonly"
              @click.prevent.stop="
                evh_7127218473441393_clickPreventStop($event, {})
              "
            >
            </ItpButton>
          </ItpBox>
          <template #expander>
            <UcContractRentalCard
              :rental="contract.rental"
              v-if="contract.rental"
            >
            </UcContractRentalCard>
            <UcContractRentalServices
              :rental="contract.rental"
              :reload="lastLoaded"
              v-if="contract.rental"
              @reload="evh_4198689235226718_reload($event, {})"
            >
            </UcContractRentalServices>
          </template>
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
